import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import type { DataResponse } from "@/api/schemas";
import { pravaZMe, type Prava } from "@/lib/prava";

/**
 * Auth hooky — session cookie je httponly, frontend ju nečíta.
 * Jediný zdroj pravdy o prihlásení je `GET /api/auth/me`.
 *
 * Typy sú lokálne — `types.gen.ts` ich zatiaľ neobsahuje (regenerujú
 * sa cez `npm run gen:types` až po ďalšom OpenAPI sync-u).
 */

export interface UserInfo {
  username: string;
  meno: string;
  je_admin: boolean;
  /** `rozpoctar` | `obchodnik` (KOLO 231); admin má všetky práva. */
  rola?: string;
}

export interface MeResponse {
  auth_enabled: boolean;
  user: UserInfo | null;
  /** Čo smie prihlásený upravovať — počíta server (KOLO 231). */
  prava?: Prava;
}

export interface LoginRequest {
  username: string;
  password: string;
}

/** Kto som + či je auth zapnutá (frontend gate). Verejný endpoint. */
export function useMe(): UseQueryResult<DataResponse<MeResponse>> {
  return useQuery({
    queryKey: queryKeys.auth.me(),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<MeResponse>>("/auth/me");
      return data;
    },
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * Práva prihláseného (KOLO 231) — zo `/me`; kým nedobehne a bez
 * prihlásenia plné (server zmenu bez práva odmietne tak či tak).
 */
export function usePrava(): Prava {
  const { data } = useMe();
  return pravaZMe(data?.data);
}

/** Prihlásenie — server nastaví httponly cookie, my refreshneme cache. */
export function useLogin(): UseMutationResult<
  DataResponse<MeResponse>,
  Error,
  LoginRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: LoginRequest) => {
      const { data } = await api.post<DataResponse<MeResponse>>(
        "/auth/login",
        payload,
      );
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.auth.me(), data);
      // Po logine invalidujeme všetko — dáta sa mohli medzitým zmeniť
      // a queries mohli zlyhať kým user nebol prihlásený.
      void qc.invalidateQueries();
    },
  });
}

/** Odhlásenie — revokuje session, vyčistí cache a presmeruje na /login. */
export function useLogout(): UseMutationResult<void, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post("/auth/logout");
    },
    onSuccess: () => {
      qc.clear();
      window.location.assign("/login");
    },
  });
}
